import axios, { AxiosInstance, AxiosResponse } from "axios";
import md5 from "js-md5";
import Message from "@/components/message";
import { useGlobalStore } from "@/stores";

interface Data {
  [key: string]: string | number | Data | any[];
}

export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

// 创建 axios 实例
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    // timeout: 10000, // 10秒超时
    baseURL: import.meta.env.VITE_API_BASE_URL,
  });

  // 请求拦截器
  instance.interceptors.request.use(
    (config: any) => {
      const timestamp = Date.parse(`${new Date()}`) / 1000;
      const key = import.meta.env.VITE_API_SIGN_KEY;
      const sign = md5(`${config.url}${timestamp}${key}`);

      // 设置请求头
      const { token } = useGlobalStore.getState();
      config.headers = {
        ...config.headers,
        timestamp: `${timestamp}`,
        sign,
        "Content-Type": "application/json",
        Authorization: token,
      };

      // 显示loading
      const { showLoading } = useGlobalStore.getState();
      showLoading();

      return config;
    },
    (error) => {
      // 请求错误时隐藏loading
      const { hideLoading } = useGlobalStore.getState();
      hideLoading();

      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const res = response.data;

      const { hideLoading, handleLogout } = useGlobalStore.getState();
      // 隐藏loading
      hideLoading();

      if (res.code == 401) {
        handleLogout();
      }

      if (res.code !== 0) {
        Message.error({ content: res.message });
        throw res;
      }

      return response;
    },
    (error) => {
      // 隐藏loading
      const { hideLoading } = useGlobalStore.getState();
      hideLoading();
      Message.error({ content: error.message });

      return Promise.reject(error);
    }
  );

  return instance;
};

// 创建 axios 实例
const axiosInstance = createAxiosInstance();

// GET請求
export const get = <T>(
  url: string,
  data: Data = {}
): Promise<ApiResponse<T>> => {
  return axiosInstance
    .get<ApiResponse<T>>(url, { params: data })
    .then((response) => response.data);
};

// POST請求
export const post = <T>(
  url: string,
  data: Data = {}
): Promise<ApiResponse<T>> => {
  return axiosInstance
    .post<ApiResponse<T>>(url, data)
    .then((response) => response.data);
};

export { axiosInstance };
